import { supabase } from '@/lib/supabase';

// Öğrenci ödev teslim dosyalarının tutulduğu private storage bucket'ı.
const SUBMISSIONS_BUCKET = 'zehrahoca-submissions';

// Kabul edilen teslim dosya tipleri (PDF, görsel, Word belgesi).
export const ALLOWED_SUBMISSION_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const ALLOWED_SUBMISSION_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

export const MAX_SUBMISSION_SIZE = 20 * 1024 * 1024; // 20 MB

// Dosya uzantısını küçük harfle döndürür (nokta dahil). Yoksa boş string.
export function getFileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return '';
  return name.slice(dot).toLowerCase();
}

export function isSupportedSubmissionFile(file: File): boolean {
  if (!file) return false;
  if (ALLOWED_SUBMISSION_TYPES.includes(file.type)) return true;
  // Bazı tarayıcılar (özellikle mobil) MIME tipini boş veya octet-stream döndürür.
  if (!file.type || file.type === 'application/octet-stream') {
    return ALLOWED_SUBMISSION_EXTENSIONS.includes(getFileExtension(file.name));
  }
  return false;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

// Private bucket'taki teslim dosyasını tarayıcıya indirir.
export async function downloadSubmissionFile(path: string, fileName?: string): Promise<void> {
  const { data, error } = await supabase.storage.from(SUBMISSIONS_BUCKET).download(path);
  if (error) throw new Error(error.message || 'Dosya indirilemedi.');
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || path.split('/').pop() || 'teslim-dosyasi';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Private bucket'taki teslim dosyasını yeni sekmede açar (PDF/görsel önizleme).
export async function openSubmissionFile(path: string): Promise<void> {
  const { data, error } = await supabase.storage.from(SUBMISSIONS_BUCKET).download(path);
  if (error) throw new Error(error.message || 'Dosya açılamadı.');
  const url = URL.createObjectURL(data);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// --- Öğretmen ödev dosyaları (documents public bucket) ---

// Öğretmenin ödeve ekleyebileceği dosya tipleri.
export const ALLOWED_ASSIGNMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png',
  'image/jpeg',
];

export const ALLOWED_ASSIGNMENT_EXTENSIONS = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg'];

export const MAX_ASSIGNMENT_SIZE = 20 * 1024 * 1024; // 20 MB

export const ASSIGNMENTS_BUCKET = 'documents';

export function isSupportedAssignmentFile(file: File): boolean {
  if (!file) return false;
  if (ALLOWED_ASSIGNMENT_TYPES.includes(file.type)) return true;
  // Bazı tarayıcılar (özellikle mobil) MIME tipini boş veya octet-stream döndürür.
  if (!file.type || file.type === 'application/octet-stream') {
    return ALLOWED_ASSIGNMENT_EXTENSIONS.includes(getFileExtension(file.name));
  }
  return false;
}

// Dosya adını Storage path'i için güvenli hale getirir.
// Türkçe karakterleri ASCII karşılığına çevirir, boşluk ve özel karakterleri "-" yapar.
export function safeFileName(name: string): string {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  const turkishMap: Record<string, string> = {
    ç: 'c', Ç: 'c', ğ: 'g', Ğ: 'g', ı: 'i', I: 'i', İ: 'i',
    ö: 'o', Ö: 'o', ş: 's', Ş: 's', ü: 'u', Ü: 'u',
  };
  const normalized = base
    .split('')
    .map((ch) => turkishMap[ch] ?? ch)
    .join('')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `${normalized || 'dosya'}${ext.toLowerCase()}`;
}

// Public URL'deki dosyayı (ör. documents bucket'taki ders PDF'i) belirtilen isimle tarayıcıya indirir.
export async function downloadPublicFile(url: string, fileName?: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Dosya indirilemedi.');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName || '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}